import { NOISE_INTERACTIVE_LAYER_IDS } from '../core/noise-layer-runtime';
import { noiseGeometryHitsAtLngLat, normalizeNoiseHit } from './interactive-hit-dispatcher';

export function createTapResolutionManager(config) {
    var state = config.state;

    function forEachCoordinate(geometry, iterator) {
        if (!geometry || !geometry.coordinates) {
            return;
        }

        function visit(node) {
            if (!Array.isArray(node)) {
                return;
            }
            if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
                iterator(node);
                return;
            }
            node.forEach(visit);
        }

        visit(geometry.coordinates);
    }

    function getFeatureLngLatBounds(feature) {
        var minLng = Infinity;
        var minLat = Infinity;
        var maxLng = -Infinity;
        var maxLat = -Infinity;

        forEachCoordinate(feature && feature.geometry, function(coord) {
            if (coord[0] < minLng) minLng = coord[0];
            if (coord[0] > maxLng) maxLng = coord[0];
            if (coord[1] < minLat) minLat = coord[1];
            if (coord[1] > maxLat) maxLat = coord[1];
        });

        if (!isFinite(minLng) || !isFinite(minLat) || !isFinite(maxLng) || !isFinite(maxLat)) {
            return null;
        }

        return {
            minLng: minLng,
            minLat: minLat,
            maxLng: maxLng,
            maxLat: maxLat,
        };
    }

    function boundsIntersect(a, b) {
        if (!a || !b) {
            return false;
        }

        return !(a.maxLng < b.minLng || a.minLng > b.maxLng || a.maxLat < b.minLat || a.minLat > b.maxLat);
    }

    function getBoundsUnion(boundsList) {
        if (!boundsList.length) {
            return null;
        }

        return boundsList.reduce(function(result, bounds) {
            return {
                minLng: Math.min(result.minLng, bounds.minLng),
                minLat: Math.min(result.minLat, bounds.minLat),
                maxLng: Math.max(result.maxLng, bounds.maxLng),
                maxLat: Math.max(result.maxLat, bounds.maxLat),
            };
        });
    }

    function getAvailableLayerIds(layerIds) {
        var map = config.getMap();
        if (!map || !Array.isArray(layerIds)) {
            return [];
        }

        return layerIds.filter(function(layerId) {
            return !!map.getLayer(layerId);
        });
    }

    function queryRenderedOverlapFeatures(layerIds, point, unionBounds, targetBoundsList) {
        var map = config.getMap();
        var availableLayerIds = getAvailableLayerIds(layerIds);
        var renderedFeatures = [];

        if (!map || !availableLayerIds.length) {
            return renderedFeatures;
        }

        if (point) {
            try {
                renderedFeatures = renderedFeatures.concat(map.queryRenderedFeatures(point, {
                    layers: availableLayerIds
                }));
            } catch (_error) {
                renderedFeatures = [];
            }
        }

        if (unionBounds) {
            try {
                var topLeft = map.project([unionBounds.minLng, unionBounds.maxLat]);
                var bottomRight = map.project([unionBounds.maxLng, unionBounds.minLat]);
                renderedFeatures = renderedFeatures.concat(map.queryRenderedFeatures([topLeft, bottomRight], {
                    layers: availableLayerIds
                }));
            } catch (_error2) {
                renderedFeatures = [];
            }
        }

        if (!targetBoundsList.length) {
            return renderedFeatures;
        }

        return renderedFeatures.filter(function(entry) {
            var entryBounds = getFeatureLngLatBounds(entry);
            if (!entryBounds) {
                return false;
            }

            return targetBoundsList.some(function(targetBounds) {
                return boundsIntersect(targetBounds, entryBounds);
            });
        });
    }

    function getSupportingHitId(type, feature) {
        var props = feature && feature.properties ? feature.properties : {};

        if (type === 'gunb' && props.id != null) {
            return String(props.id);
        }
        if (type === 'building' && props.id_budynku != null) {
            return String(props.id_budynku);
        }
        if (type === 'parcel' && props.id_dzialki != null) {
            return String(props.id_dzialki);
        }
        if (type === 'otoczenie') {
            if (feature && feature.id != null) {
                return String(feature.id);
            }
            if (props.id != null) {
                return String(props.id);
            }
            if (props.id_bdot10k != null) {
                return String(props.id_bdot10k);
            }
            if (props.objectid != null) {
                return String(props.objectid);
            }

            var bounds = getFeatureLngLatBounds(feature);
            if (bounds) {
                return [
                    type,
                    props.layer_code || '',
                    props.category_label || '',
                    bounds.minLng.toFixed(6),
                    bounds.minLat.toFixed(6),
                    bounds.maxLng.toFixed(6),
                    bounds.maxLat.toFixed(6)
                ].join(':');
            }
        }

        if (type === 'noise') {
            return normalizeNoiseHit(feature);
        }

        return null;
    }

    function normalizeSupportingHit(type, feature) {
        var props = feature && feature.properties ? feature.properties : null;
        if (!props) {
            return null;
        }

        if (type === 'building' && props.id_budynku != null) {
            return {
                type: 'building',
                id: String(props.id_budynku),
                properties: props,
                feature: feature,
            };
        }

        if (type === 'parcel' && props.id_dzialki != null) {
            return {
                type: 'parcel',
                id: String(props.id_dzialki),
                properties: props,
                feature: feature,
            };
        }

        if (type === 'otoczenie') {
            var featureId = getSupportingHitId(type, feature);
            if (!featureId) {
                return null;
            }

            return {
                type: 'otoczenie',
                id: featureId,
                properties: props,
                feature: feature,
            };
        }

        return null;
    }

    function collectCombinedSupportingHits(features, point) {
        var map = config.getMap();
        var zoom = config.mapZoom();
        var targetFeatures = Array.isArray(features)
            ? features.filter(Boolean)
            : [];

        if (!map || !targetFeatures.length) {
            return {
                gunbFeatures: [],
                otherHits: [],
            };
        }

        var targetBoundsList = targetFeatures.map(getFeatureLngLatBounds).filter(Boolean);
        var unionBounds = getBoundsUnion(targetBoundsList);
        var gunbFeatures = [];
        var otherHits = [];
        var seenGunbIds = {};
        var seenOtherHits = {};
        var definitions = [
            {
                type: 'noise',
                enabled: getAvailableLayerIds(NOISE_INTERACTIVE_LAYER_IDS).length > 0,
                layerIds: NOISE_INTERACTIVE_LAYER_IDS
            },
            {
                type: 'gunb',
                enabled: config.isGunbActive() && zoom >= config.GunbLayer.INDIVIDUAL_ZOOM_THRESHOLD,
                layerIds: [config.GunbLayer.BASE_LAYER_IDS[0]]
            },
            {
                type: 'otoczenie',
                enabled: config.isOtoczenieActive() && zoom >= config.OtoczenieLayer.MIN_ZOOM,
                layerIds: config.getOtoczenieLayerIds()
            },
            {
                type: 'building',
                enabled: config.isBudynkiActive() && zoom >= config.BudynkiLayer.MIN_ZOOM,
                layerIds: [config.BudynkiLayer.BASE_LAYER_IDS[0]]
            },
            {
                type: 'parcel',
                enabled: config.isDzialkiActive() && zoom >= config.DzialkiLayer.MIN_ZOOM,
                layerIds: config.getParcelLayerIds()
            }
        ];

        definitions.forEach(function(definition) {
            if (!definition.enabled) {
                return;
            }

            queryRenderedOverlapFeatures(definition.layerIds, point, unionBounds, targetBoundsList).forEach(function(entry) {
                if (definition.type === 'gunb') {
                    var gunbId = getSupportingHitId('gunb', entry);
                    if (!gunbId || seenGunbIds[gunbId]) {
                        return;
                    }

                    seenGunbIds[gunbId] = true;
                    gunbFeatures.push(entry);
                    return;
                }

                var hit = normalizeSupportingHit(definition.type, entry);
                if (!hit) {
                    return;
                }

                var hitKey = hit.type + ':' + hit.id;
                if (seenOtherHits[hitKey]) {
                    return;
                }

                seenOtherHits[hitKey] = true;
                otherHits.push(hit);
            });
        });

        if (point && map.unproject) {
            var clickLngLat = map.unproject(point);
            noiseGeometryHitsAtLngLat(config.getNoiseFeatures, clickLngLat).forEach(function(hit) {
                var hitKey = hit.type + ':' + hit.id;
                if (!seenOtherHits[hitKey]) {
                    seenOtherHits[hitKey] = true;
                    otherHits.push(hit);
                }
            });
        }

        return {
            gunbFeatures: gunbFeatures,
            otherHits: otherHits,
        };
    }

    function appendRenderedPointOtoczenieHits(hits, point) {
        var map = config.getMap();
        var zoom = config.mapZoom();
        if (!map || !point || !config.isOtoczenieActive() || zoom < config.OtoczenieLayer.MIN_ZOOM) {
            return hits;
        }

        var seenIds = {};
        hits.forEach(function(hit) {
            seenIds[hit.type + ':' + hit.id] = true;
        });

        try {
            map.queryRenderedFeatures(point, {
                layers: getAvailableLayerIds(config.getOtoczenieLayerIds())
            }).forEach(function(feature) {
                var hit = normalizeSupportingHit('otoczenie', feature);
                var hitKey = hit ? hit.type + ':' + hit.id : null;
                if (!hit || seenIds[hitKey]) {
                    return;
                }

                seenIds[hitKey] = true;
                hits.push(hit);
            });
        } catch (_error3) {
            return hits;
        }

        return hits;
    }

    function abortCurrentRequest() {
        var controller = state.getAbortTapResolver();
        if (controller) controller.abort();
    }

    function requestMobileTapResolution(latlng, point) {
        return config.dispatchPointQuery(latlng, point);
    }

    function requestTransactionTapResolution(feature, latlng, point) {
        var props = feature && feature.properties;
        if (!props || !props.tran_lokalny_id_iip) return;
        return config.dispatchPointQuery(latlng, point, [{
            type: 'transaction', id: String(props.tran_lokalny_id_iip),
            properties: props, feature: feature,
        }]);
    }

    return {
        abortCurrentRequest: abortCurrentRequest,
        collectCombinedSupportingHits: collectCombinedSupportingHits,
        requestMobileTapResolution: requestMobileTapResolution,
        requestTransactionTapResolution: requestTransactionTapResolution,
    };
}
